import {
  SeriesEndConfirmDetails,
  SeriesEndLessonStartedDetails,
  SeriesEndPastDetails,
  localDateAt,
} from "@edu/shared";
import type { EndSeriesResponse, ScheduleEntry } from "@edu/shared";
import { lessonCountText, pick } from "../../lib/plurals.ts";
import { localDateText } from "./common.ts";

/**
 * Слова завершения серии пары с даты: строка в карточке занятия, панель на
 * экране серии, подтверждение с перечнем потерь, отказы и строка итога.
 *
 * Чистые функции с тестами, а не строки по месту, по той же причине, что у
 * `group-text.ts`: здесь склоняются числа и выбирается форма фразы по числу
 * дат, а это ровно то, что на глаз пишется неверно. Разбор поведения в
 * `docs/schedule-design.md`, раздел 5.6.
 *
 * ⚠️ **Дата никогда не стоит последней во фразе.** `localDateText` даёт короткий
 * месяц с точкой («12 окт.»), и точка сокращения перед концом предложения
 * читается как опечатка. В подписи кнопки («Завершить с 12 окт.») это законно:
 * там предложения нет. Проверяется тестом на каждой фразе.
 *
 * ⚠️ **Одна дата или несколько меняют форму всей фразы**, а не одного слова:
 * «у занятия 12 окт.» против «у занятий 12 окт. и 19 окт.», «к нему» против «к
 * ним». Форму выбирает длина присланного списка. Сервер отдаёт `paidAhead` и
 * `paidBefore` уже различными датами, поэтому две оплаты в один день это одна
 * дата: «у занятия 12 окт.», и в строке последствий одна «Отметка скроется».
 * Это неточность в числе, а не в датах, и она принята.
 *
 * ⚠️ Склонения отметок и сообщений живут здесь, а не в `lib/plurals.ts`: тот
 * подошёл к потолку в 400 строк. Форму выбирает его же `pick`, чтобы правило
 * выбора осталось одно на весь кабинет.
 */

/**
 * Дата, с которой карточка предлагает убрать занятие и все следующие, либо
 * `null`, если строки быть не должно.
 *
 * Дата это МЕСТО занятия в зоне серии, а не исходная дата перенесённого: сервер
 * режет по дню в зоне серии, и человек нажимал на то занятие, которое видит.
 *
 * `null` у занятия группы (у пучка своя ручка роспуска, кнопки там нет), у
 * строки не из серии, без серии или без её зоны, у отменённого и уехавшего
 * (их убирать нечего) и у уже начавшегося: прошлое завершение не трогает, и
 * строка вела бы в отказ.
 */
export function seriesCutDateOf(entry: ScheduleEntry, now: number): string | null {
  if (entry.group !== undefined) return null;
  if (entry.source !== "series" || entry.seriesId === null) return null;
  if (entry.seriesTimeZone === null) return null;
  if (entry.state === "cancelled" || entry.state === "moved_away") return null;
  if (entry.at <= now) return null;
  return localDateAt(entry.at, entry.seriesTimeZone);
}

/* ───────────────────────────────── склонения ──────────────────────────────── */

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** «одна отметка», «2 отметки», «5 отметок». Единица словом: «1 отметка» вслух не говорят. */
export function markCountText(count: number): string {
  if (count === 1) return "одна отметка";
  return `${count} ${pick(count, "отметка", "отметки", "отметок")}`;
}

/** «одно сообщение», «2 сообщения», «5 сообщений». */
export function messageCountText(count: number): string {
  if (count === 1) return "одно сообщение";
  return `${count} ${pick(count, "сообщение", "сообщения", "сообщений")}`;
}

/** «один урок», «2 урока»: обёртка над общим счётчиком ради единицы словом. */
export function lessonsCountText(count: number): string {
  return count === 1 ? "один урок" : lessonCountText(count);
}

/**
 * Различные даты словами по порядку: «12 окт.», «12 окт. и 19 окт.»,
 * «12 окт., 19 окт. и 26 окт.».
 */
export function datesText(dates: readonly string[], now: number): string {
  const words = [...new Set(dates)].sort().map((d) => localDateText(d, now));
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} и ${words[words.length - 1]}`;
}

/* ──────────────────────────────── первый шаг ──────────────────────────────── */

/** Карточка, первый шаг: что исчезнет с даты нажатого занятия. */
export function cardAskText(from: string, now: number): string {
  return (
    `Занятия этой серии с ${localDateText(from, now)} и дальше у вас и у ученика исчезнут ` +
    "без пометки об отмене, предупредите его сами. Прошедшие занятия, уроки и отметки " +
    "останутся. Если это первое занятие серии, она удалится целиком."
  );
}

/** Причина, набранная в форме отмены, с завершением не уезжает никуда. */
export const REASON_NOT_SENT = "Причину ученик не увидит.";

export const PANEL_TITLE = "Если занятия закончились";

/** Экран серии, первый шаг. Дата не названа: её выбирает сервер. */
export const PANEL_ASK_TEXT =
  "Будущие занятия у вас и у ученика исчезнут без пометки об отмене, предупредите его сами. " +
  "Прошедшие занятия, уроки и отметки останутся, а если сегодня занятие уже идёт или прошло, " +
  "останутся и занятия этого дня. Если занятий ещё не было, серия удалится целиком.";

/* ──────────────────────────── второй шаг, перечень ─────────────────────────── */

/**
 * Вводная удаления без причины. «Занятий ещё не было» врёт у серии, которой
 * сдвинули «Начало»: отметки, оплаты и переписка прошлых занятий тогда лежат на
 * датах раньше него, и сервер всё равно удаляет серию целиком.
 */
export const DELETES_LEAD = "Серия удалится целиком.";
export const REFRESHED_LEAD = "Пока вы читали, список изменился, проверьте его ещё раз.";

/**
 * Повторный показ, в котором сменился сам исход: человек подтверждал удаление,
 * а выходит закрытие датой, или наоборот. Строка называет новый исход, иначе
 * при пустом перечне повтор выглядел бы тем же вопросом без причины.
 */
export function flippedLead(deletes: boolean, hasPoints: boolean): string {
  const next = deletes
    ? "теперь она удалится целиком"
    : "удаляться она уже не будет, у неё заполнится «Окончание»";
  const check = hasPoints ? " Проверьте список ещё раз." : "";
  return `Пока вы читали, серия изменилась: ${next}.${check}`;
}

/** Пункт перечня. `rows` есть только у уроков: их показываем списком с датами. */
export interface ConfirmPoint {
  text: string;
  rows: { id: string; text: string }[];
}

/** Второй шаг целиком: вводные строки, перечень по порядку и одна строка последствий. */
export interface ConfirmView {
  lead: string[];
  points: ConfirmPoint[];
  /** `null`, если перечень пуст (повторный показ, где пропало всё, кроме удаления). */
  outcome: string | null;
}

/**
 * Второй шаг. `previous` это перечень, который человек уже подтвердил, если
 * сервер переспросил после `force`, иначе `null`.
 */
export function confirmView(
  details: SeriesEndConfirmDetails,
  previous: SeriesEndConfirmDetails | null,
  now: number,
): ConfirmView {
  const points = confirmPoints(details, now);
  // Строка смены исхода стоит вместо вводной: она сама называет новый исход
  const lead: string[] = [];
  if (previous !== null && previous.deletes !== details.deletes) {
    lead.push(flippedLead(details.deletes, points.length > 0));
  } else {
    if (details.deletes) lead.push(DELETES_LEAD);
    if (previous !== null) lead.push(REFRESHED_LEAD);
  }
  return { lead, points, outcome: outcomeText(details) };
}

function confirmPoints(details: SeriesEndConfirmDetails, now: number): ConfirmPoint[] {
  const points: ConfirmPoint[] = [];
  const { lessons, paidAhead, paidBefore, otherMarks, chatMessages } = details;
  if (lessons.length > 0) {
    const n = lessons.length;
    const ready = pick(n, "подготовлен", "подготовлено", "подготовлено");
    points.push({
      text: `Уже ${ready} ${lessonsCountText(n)}:`,
      rows: [...lessons]
        .sort((a, b) => a.on.localeCompare(b.on))
        .map((l) => ({ id: l.id, text: `${localDateText(l.on, now)} · ${l.title}` })),
    });
  }
  if (paidAhead.length > 0) {
    const at = paidAhead.length === 1 ? "занятия" : "занятий";
    points.push({
      text:
        `Оплата вперёд отмечена у ${at} ${datesText(paidAhead, now)}, ` +
        "учтите это при расчёте с учеником.",
      rows: [],
    });
  }
  if (paidBefore.length > 0) {
    const one = paidBefore.length === 1;
    const at = one ? "прошедшего занятия" : "прошедших занятий";
    points.push({
      text:
        `Оплата отмечена у ${at} ${datesText(paidBefore, now)}, ` +
        `к ${one ? "нему" : "ним"} не было урока, учтите это при расчёте с учеником.`,
      rows: [],
    });
  }
  const parts: string[] = [];
  if (otherMarks > 0) parts.push(`${markCountText(otherMarks)} о посещении`);
  if (chatMessages > 0) parts.push(`${messageCountText(chatMessages)} в чате`);
  if (parts.length > 0) {
    // При удалении исчезают ВСЕ занятия серии, а не только будущие, и фраза
    // обязана говорить именно это. «Ещё» только не первым пунктом: первым оно
    // ссылалось бы на пустоту. Подлежащее повторяется целиком, а не «у них»:
    // перед пунктом может стоять список уроков, и местоимение ушло бы к ним
    const whose = details.deletes ? "у занятий серии" : "у занятий, которые исчезнут,";
    const head = points.length > 0 ? `Ещё ${whose}` : capitalize(whose);
    points.push({ text: `${head} есть ${parts.join(" и ")}.`, rows: [] });
  }
  return points;
}

/**
 * Строка последствий из того, что есть в перечне. Подлежащее и глагол
 * согласуются по числу: одна отметка это «Отметка удалится», переписка всегда
 * одна, вместе они «удалятся».
 */
function outcomeText(details: SeriesEndConfirmDetails): string | null {
  const { lessons, paidAhead, paidBefore, otherMarks, chatMessages, deletes } = details;
  const out: string[] = [];
  if (lessons.length === 1) {
    out.push("Урок уйдёт в корзину, вернуть его оттуда можно в течение тридцати дней.");
  } else if (lessons.length > 1) {
    out.push("Уроки уйдут в корзину, вернуть их оттуда можно в течение тридцати дней.");
  }
  const marks = paidAhead.length + paidBefore.length + otherMarks;
  const chat = chatMessages > 0;
  if (marks > 0 || chat) {
    const mark = marks === 1 ? "Отметка" : "Отметки";
    const what = marks > 0 && chat ? `${mark} и переписка` : marks > 0 ? mark : "Переписка";
    const many = what !== "Отметка" && what !== "Переписка";
    const them = many ? "их" : "её";
    out.push(
      deletes
        ? `${what} ${many ? "удалятся" : "удалится"} вместе с серией, вернуть ${them} будет нельзя.`
        : `${what} ${many ? "скроются" : "скроется"} вместе с занятиями и ` +
            `${many ? "вернутся" : "вернётся"}, если убрать «Окончание».`,
    );
  }
  return out.length > 0 ? out.join(" ") : null;
}

/* ─────────────────────────────── отказы и итог ────────────────────────────── */

/** Явная дата задела начавшееся занятие: сервер назвал самую раннюю возможную. */
export function pastText(from: string, now: number): string {
  return (
    `Завершить серию можно только с ${localDateText(from, now)} включительно: одно из ` +
    "занятий раньше этой даты уже началось или прошло."
  );
}

/**
 * Границу держит проведённый урок. Совет рабочий: урок в корзине границу не
 * держит, сервер его при подсчёте пропускает.
 */
export function lessonStartedText(a: { on: string; from: string }, now: number): string {
  return (
    `Урок к занятию ${localDateText(a.on, now)} уже проведён, поэтому завершить серию можно ` +
    `только с ${localDateText(a.from, now)} включительно. Если урок провели заранее по ` +
    "ошибке, уберите его в корзину, и серию можно будет завершить раньше."
  );
}

/** Подпись кнопки повтора. Дата последней законна: это подпись, а не фраза. */
export function endFromLabel(from: string, now: number): string {
  return `Завершить с ${localDateText(from, now)}`;
}

/**
 * `unchanged`: конец серии уже раньше даты, с которой просили завершить.
 * Называется сам конец, а не сравнение: дату, с которой сравнивали, человек
 * не видел (на экране серии её выбирал сервер).
 */
export function unchangedText(endsOn: string | null, now: number): string {
  if (endsOn === null) return "Менять ничего не пришлось.";
  return `У серии уже стоит «Окончание» ${localDateText(endsOn, now)}, менять ничего не пришлось.`;
}
export const BAD_CONFIRM_TEXT =
  "Не получилось показать, что пропадёт. Обновите страницу и попробуйте снова.";
export const BAD_RETRY_TEXT = "Обновите страницу и попробуйте снова.";

/**
 * Строка итога закрытия датой.
 *
 * `heldBy` объясняет конец, только когда он и есть последнее оставшееся занятие
 * (`heldBy.on === lastOn`): иначе причина называла бы день, которого в
 * расписании уже нет.
 */
export function endedText(
  res: Pick<EndSeriesResponse, "heldBy" | "lastOn">,
  now: number,
): string {
  const { heldBy, lastOn } = res;
  if (lastOn === null) return "Серия завершена, занятий по ней больше не будет.";
  const last = localDateText(lastOn, now);
  if (heldBy !== null && heldBy.on === lastOn) {
    const head = `Серия завершена, занятия по ${last} включительно остаются`;
    return heldBy.kind === "started"
      ? `${head}: в этот день занятие уже началось.`
      : `${head}: урок к последнему из них уже проведён.`;
  }
  return `Серия завершена, последнее занятие ${last} остаётся в расписании.`;
}

/* ────────────────────────────── разбор отказов ────────────────────────────── */

type Details = Readonly<Record<string, unknown>>;

/** Перечень `series_end_confirm`, либо `null`, если тело кривое. */
export function confirmDetailsOf(details: Details): SeriesEndConfirmDetails | null {
  const parsed = SeriesEndConfirmDetails.safeParse(details);
  return parsed.success ? parsed.data : null;
}

/** Самая ранняя возможная дата из `series_end_past`, либо `null`. */
export function safeFromOf(details: Details): string | null {
  const parsed = SeriesEndPastDetails.safeParse(details);
  return parsed.success ? parsed.data.from : null;
}

/** Даты из `lesson_started` этой ручки, либо `null`. */
export function lessonStartedOf(
  details: Details,
): { occursOn: string; on: string; from: string } | null {
  const parsed = SeriesEndLessonStartedDetails.safeParse(details);
  if (!parsed.success) return null;
  const { occursOn, on, from } = parsed.data;
  return { occursOn, on, from };
}
